import React, { createContext, useContext, useState, useRef, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface AISuggestion {
  id: string;
  content: string;
  confidence: number;
  source: string;
  metadata: unknown;
  timestamp: string;
  aiSource: {
    modelUsed: string;
    provider: string;
    generatedAt: string;
  };
}

interface AISuggestionsState {
  currentSuggestion: AISuggestion | null;
  loading: boolean;
  retryCount: number;
  circuitBreakerOpen: boolean;
  conversationId: string | null;
  lastCustomerMessageId: string | null;
}

interface AISuggestionsContextType {
  // State
  currentSuggestion: AISuggestion | null;
  loading: boolean;
  retryCount: number;
  circuitBreakerOpen: boolean;
  
  // Actions
  setCurrentSuggestion: (suggestion: AISuggestion | null) => void;
  setLoading: (loading: boolean) => void;
  setRetryCount: (count: number) => void;
  setCircuitBreakerOpen: (open: boolean) => void;
  clearSuggestion: () => void;
  clearSuggestionOnCustomerMessage: (messageId: string) => void;
  setConversationId: (conversationId: string | null) => void;
  generateAISuggestion: (customerMessage: string, options?: { conversationId?: string; customerId?: string; customerPhone?: string; tenantId?: string; bufferedMessages?: unknown[] }) => Promise<AISuggestion | null>;
  
  // Utilities
  persistSuggestion: (suggestion: AISuggestion) => void;
  recoverSuggestion: () => AISuggestion | null;
  pollForAISuggestion: (conversationId: string, tenantId: string) => Promise<AISuggestion | null>;
}

const AISuggestionsContext = createContext<AISuggestionsContextType | undefined>(undefined);

const STORAGE_KEY = 'ai_suggestions_state';
const HOOK_INSTANCE_KEY = 'ai_suggestions_hook_instance';

export const AISuggestionsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AISuggestionsState>({
    currentSuggestion: null,
    loading: false,
    retryCount: 0,
    circuitBreakerOpen: false,
    conversationId: null,
    lastCustomerMessageId: null,
  });

  const persistenceRef = useRef<AISuggestion | null>(null);
  const setTimeRef = useRef<number | null>(null);
  const instanceIdRef = useRef<string>(`instance-${Date.now()}-${Math.random()}`);

  // Persist suggestion to localStorage
  const persistSuggestion = (suggestion: AISuggestion) => {
    try {
      const persistData = {
        suggestion,
        instanceId: instanceIdRef.current,
        timestamp: Date.now(),
        conversationId: state.conversationId,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(persistData));
      persistenceRef.current = suggestion;
      setTimeRef.current = Date.now();
      
      console.log('💾 AI SUGGESTION PERSIST: Saved to localStorage', {
        suggestionId: suggestion.id,
        instanceId: instanceIdRef.current,
        conversationId: state.conversationId,
      });
    } catch (error) {
      console.error('Failed to persist AI suggestion:', error);
    }
  };

  // Recover suggestion from localStorage
  const recoverSuggestion = (): AISuggestion | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;

      const persistData = JSON.parse(stored);
      const timeSinceSet = Date.now() - persistData.timestamp;
      const maxAge = 300000; // 5 minutes
      
      if (timeSinceSet > maxAge) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }

      if (persistData.conversationId === state.conversationId) {
        console.log('🔄 AI SUGGESTION RECOVER: Restored from localStorage', {
          suggestionId: persistData.suggestion.id,
          timeSinceSet,
          conversationId: persistData.conversationId,
        });
        return persistData.suggestion;
      }
      
      return null;
    } catch (error) {
      console.error('Failed to recover AI suggestion:', error);
      return null;
    }
  };

  const setCurrentSuggestion = useCallback((suggestion: AISuggestion | null) => {
    console.log('🎯 AI CONTEXT: Setting suggestion', {
      suggestionId: suggestion?.id,
      hasContent: !!suggestion?.content,
      instanceId: instanceIdRef.current,
      timestamp: new Date().toISOString(),
    });

    setState(prev => ({ ...prev, currentSuggestion: suggestion }));
    
    if (suggestion) {
      persistSuggestion(suggestion);
    } else {
      // Clear persistence when suggestion is explicitly cleared
      localStorage.removeItem(STORAGE_KEY);
      persistenceRef.current = null;
      setTimeRef.current = null;
    }
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    setState(prev => ({ ...prev, loading }));
  }, []);

  const setRetryCount = useCallback((retryCount: number) => {
    setState(prev => ({ ...prev, retryCount }));
  }, []);

  const setCircuitBreakerOpen = useCallback((circuitBreakerOpen: boolean) => {
    setState(prev => ({ ...prev, circuitBreakerOpen }));
  }, []);

  const clearSuggestion = useCallback(() => {
    console.log('🚀 AI CONTEXT: Clearing suggestion', {
      instanceId: instanceIdRef.current,
      timestamp: new Date().toISOString(),
    });
    setCurrentSuggestion(null);
  }, [setCurrentSuggestion]);

  const clearSuggestionOnCustomerMessage = useCallback((messageId: string) => {
    console.log('🗨️ AI CONTEXT: Customer message detected, clearing existing suggestion', {
      messageId,
      currentSuggestionId: state.currentSuggestion?.id,
      instanceId: instanceIdRef.current,
    });
    
    // Clear suggestion if there's one active
    if (state.currentSuggestion) {
      setCurrentSuggestion(null);
      setState(prev => ({ ...prev, lastCustomerMessageId: messageId }));
    }
  }, [state.currentSuggestion, setCurrentSuggestion]);

  const setConversationId = useCallback((conversationId: string | null) => {
    setState(prev => ({ 
      ...prev, 
      conversationId, 
      lastCustomerMessageId: null // Reset when conversation changes
    }));
  }, []);

  // Register hook instance to prevent conflicts
  useEffect(() => {
    localStorage.setItem(HOOK_INSTANCE_KEY, instanceIdRef.current);
    console.log('🔧 AI CONTEXT: Registered instance', {
      instanceId: instanceIdRef.current,
    });

    return () => {
      const currentInstance = localStorage.getItem(HOOK_INSTANCE_KEY);
      if (currentInstance === instanceIdRef.current) {
        localStorage.removeItem(HOOK_INSTANCE_KEY);
      }
    };
  }, []);

  // 🎯 [AI-ASSIST-FIX] Poll for AI suggestions in platform_metadata
  // This runs when a new customer message is detected in suggestions mode
  const pollForAISuggestion = useCallback(async (conversationId: string, tenantId: string): Promise<AISuggestion | null> => {
    console.log('📡 [AI-ASSIST] Starting poll for AI suggestion');

    const maxAttempts = 10; // Poll for up to 10 seconds
    const pollInterval = 1000; // 1 second between polls

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      console.log(`🔍 [AI-ASSIST] Poll attempt ${attempt}/${maxAttempts}`);

      try {
        // Query conversation for platform_metadata
        const { data: conversation, error } = await supabase
          .from('conversations')
          .select('platform_metadata')
          .eq('id', conversationId)
          .single();

        if (error) {
          console.error('❌ [AI-ASSIST] Error querying platform_metadata:', error);
          continue;
        }

        const platformMetadata = conversation?.platform_metadata;
        if (platformMetadata?.current_ai_suggestion?.status === 'pending') {
          const suggestion = platformMetadata.current_ai_suggestion;
          console.log('💡 [AI-ASSIST] AI suggestion found!', suggestion.content?.substring(0, 50));

          // Create AISuggestion object
          const aiSuggestion: AISuggestion = {
            id: `suggestion-${Date.now()}`,
            content: suggestion.content,
            confidence: 0.9,
            source: 'workflow-executor',
            metadata: suggestion,
            timestamp: suggestion.timestamp || new Date().toISOString(),
            aiSource: {
              modelUsed: suggestion.modelUsed || 'unknown',
              provider: suggestion.provider || 'unknown',
              generatedAt: suggestion.generatedAt || suggestion.timestamp || new Date().toISOString()
            }
          };

          // Mark as displayed to prevent re-showing
          await supabase
            .from('conversations')
            .update({
              platform_metadata: {
                ...platformMetadata,
                current_ai_suggestion: {
                  ...suggestion,
                  status: 'displayed'
                }
              }
            })
            .eq('id', conversationId);

          return aiSuggestion;
        }
      } catch (pollError) {
        console.error('💥 [AI-ASSIST] Exception during poll:', pollError);
      }

      // Wait before next attempt (unless this was the last attempt)
      if (attempt < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      }
    }

    console.warn('⏰ [AI-ASSIST] Polling timeout - no suggestion found after 10 seconds');
    return null;
  }, []);

  // Generate AI suggestion function
  const generateAISuggestion = useCallback(async (
    customerMessage: string,
    options?: { conversationId?: string; customerId?: string; customerPhone?: string; tenantId?: string; bufferedMessages?: unknown[] }
  ): Promise<AISuggestion | null> => {
    const { conversationId, customerId, customerPhone, tenantId, bufferedMessages } = options || {};
    
    console.log('🚀 AI CONTEXT: generateAISuggestion called', {
      customerMessage: customerMessage?.substring(0, 50),
      conversationId: conversationId || state.conversationId,
      customerId,
      customerPhone,
      tenantId,
      timestamp: new Date().toISOString()
    });

    if (state.circuitBreakerOpen) {
      console.log('🚫 AI CONTEXT: Circuit breaker is open, skipping request');
      return null;
    }

    if (!customerMessage?.trim()) {
      console.log('🚫 AI CONTEXT: No customer message provided');
      return null;
    }

    try {
      setLoading(true);
      
      // Get tenant schema first
      if (!tenantId) {
        console.error('❌ AI CONTEXT: No tenant ID provided');
        return null;
      }

      const { data: tenantData } = await supabase
        .from('tenants')
        .select('schema_name')
        .eq('id', tenantId)
        .single();

      const tenantSchema = tenantData?.schema_name;
      if (!tenantSchema) {
        console.error('❌ AI CONTEXT: No tenant schema found');
        return null;
      }

      // Get active agent and workflow from tenant database
      let agentId = null;
      let workflowId = null;
      
      try {
        // Query for active agents using RPC function
        const { data: agentData, error: agentError } = await supabase.rpc('get_tenant_ai_agents_v2', {
          tenant_schema: tenantSchema,
          p_tenant_id: tenantId
        });
          
        if (agentError) {
          console.warn('⚠️ AI CONTEXT: Could not find active agent:', agentError);
        } else if (agentData && Array.isArray(agentData) && agentData.length > 0) {
          // Find first active agent
          const activeAgent = agentData.find((agent: { is_active?: boolean; [key: string]: unknown }) => agent.is_active === true);
          console.log('🤖 AI CONTEXT: Found agents', {
            totalAgents: agentData.length,
            activeAgent: activeAgent ? { id: activeAgent.id, name: activeAgent.agent_name } : null
          });
          
          if (activeAgent) {
            agentId = activeAgent.id;
            
            // Query for workflows using the correct parameters
            const { data: workflowData, error: workflowError } = await supabase.rpc('get_tenant_workflows', {
              tenant_schema: tenantSchema,
              agent_id_filter: agentId,
              active_only: true,
              limit_count: 100
            });
            
            if (workflowError) {
              console.warn('⚠️ AI CONTEXT: Could not find active workflow for agent:', workflowError);
            } else if (workflowData && Array.isArray(workflowData) && workflowData.length > 0) {
              // workflowData is now a direct array - find workflow for this specific agent that is active
              const activeWorkflow = workflowData.find((workflow: { is_active?: boolean; status?: string; [key: string]: unknown }) => 
                workflow.agent_id === agentId && (workflow.status === 'active' || !workflow.status)
              );
              console.log('🔄 AI CONTEXT: Found workflows', {
                totalWorkflows: workflowData.length,
                agentId,
                activeWorkflow: activeWorkflow ? { id: activeWorkflow.id, name: activeWorkflow.name } : null
              });
              
              if (activeWorkflow) {
                workflowId = activeWorkflow.id;
              }
            }
          }
        }
      } catch (queryError) {
        console.error('❌ AI CONTEXT: Error querying agent/workflow:', queryError);
      }

      if (!agentId || !workflowId) {
        console.error('❌ AI CONTEXT: No active agent or workflow found', { agentId, workflowId, tenantSchema });
        return null;
      }

      // Prepare request body for workflow-executor with proper structure
      // 🎯 [AI-ASSIST-FIX-V2] Include bufferedMessages to bypass buffer check for mode switch scenarios
      const workflowExecutionContext: Record<string, unknown> = {
        tenantId,
        tenantSchema: tenantSchema,
        conversationId: conversationId || state.conversationId,
        customerId: customerId || '',
        customerPhone: customerPhone || '',
        messageContent: customerMessage.trim(),
        messageType: 'text',
        agentId,
        workflowId,
        aiMode: 'suggestions'
      };

      // Add bufferedMessages if provided (for mode switch with old unanswered messages)
      if (bufferedMessages && bufferedMessages.length > 0) {
        workflowExecutionContext.bufferedMessages = bufferedMessages.map(msg => ({
          content: msg.content,
          sender_type: msg.sender_type,
          created_at: msg.created_at,
          id: msg.id
        }));
        console.log('🎯 [AI-ASSIST-MODE-SWITCH] Including bufferedMessages to bypass buffer check', {
          messageCount: bufferedMessages.length
        });
      }

      console.log('🚀 AI CONTEXT: Making request to workflow-executor', {
        ...workflowExecutionContext,
        agentFound: !!agentId,
        workflowFound: !!workflowId
      });

      // Use workflow-executor with proper context wrapper
      const { data, error } = await supabase.functions.invoke('workflow-executor', {
        body: { context: workflowExecutionContext }
      });

      if (error) {
        throw new Error(`Edge function error: ${error.message}`);
      }
      
      // Extract AI response from the correct location in workflow-executor response
      const aiResponse = data.executionResult?.aiResponse?.response || data.response;
      
      if (!data.success || !aiResponse) {
        console.log('🚫 AI CONTEXT: No suggestion generated', data);
        return null;
      }

      // Create suggestion object from workflow-executor response
      const suggestion: AISuggestion = {
        id: `suggestion-${Date.now()}`,
        content: aiResponse,
        confidence: data.executionResult?.aiResponse?.confidence || data.confidence || 0.8,
        source: 'workflow-executor',
        metadata: data.executionResult?.aiResponse?.metadata || data.metadata || {},
        timestamp: new Date().toISOString(),
        aiSource: {
          modelUsed: data.executionResult?.aiResponse?.model_used || data.model_used || 'unknown',
          provider: 'openai',
          generatedAt: new Date().toISOString(),
        }
      };

      console.log('✅ AI CONTEXT: Suggestion generated successfully', {
        suggestionId: suggestion.id,
        contentLength: suggestion.content.length
      });

      setCurrentSuggestion(suggestion);
      return suggestion;

    } catch (error) {
      console.error('❌ AI CONTEXT: Error generating suggestion:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [state.circuitBreakerOpen, state.conversationId, setLoading, setCurrentSuggestion]);

  const contextValue: AISuggestionsContextType = {
    currentSuggestion: state.currentSuggestion,
    loading: state.loading,
    retryCount: state.retryCount,
    circuitBreakerOpen: state.circuitBreakerOpen,
    setCurrentSuggestion,
    setLoading,
    setRetryCount,
    setCircuitBreakerOpen,
    clearSuggestion,
    clearSuggestionOnCustomerMessage,
    setConversationId,
    generateAISuggestion,
    persistSuggestion,
    recoverSuggestion,
    pollForAISuggestion,
  };

  return (
    <AISuggestionsContext.Provider value={contextValue}>
      {children}
    </AISuggestionsContext.Provider>
  );
};

export const useAISuggestionsContext = () => {
  const context = useContext(AISuggestionsContext);
  if (context === undefined) {
    throw new Error('useAISuggestionsContext must be used within an AISuggestionsProvider');
  }
  return context;
};